import React from 'react';
import { fmtM, fmtPct } from '../../services/deltaService';
import { useSortableRows, SortableTh } from '../../components/tables/SortableTable';
import { AVAIL, Chip, Empty, Label, StackedBar, Tile } from './DeltaUI';

// Shared presentation for the asset nexus, used by BOTH the main Delta module and the
// read-only shareholder portal.
//
// Extracted rather than copied on purpose. The two surfaces are deliberately separate at
// the auth and routing layer, but they must never disagree about what the book looks
// like: the numbers already come from one server-side implementation, and this keeps the
// rendering single too. A second copy would drift, and the reader least equipped to
// notice a stale or differently-bucketed figure is the one using the viewer portal.
//
// Every component here is pure — it takes already-aggregated data and draws it. No
// fetching, no recomputation, no write controls.

const colorFor = (key) => AVAIL[key]?.bar
  || ({ liquid: '#2E4E86', semi: '#8C6D3F', illiquid: '#8A8A80' })[key]
  || '#8A8A80';

const LIQ_CHIP = {
  liquid: 'bg-blue-500/10 text-blue-700 dark:text-blue-400',
  semi: 'bg-amber-600/10 text-amber-800 dark:text-amber-500',
  illiquid: 'bg-gray-500/10 text-gray-600 dark:text-gray-400',
};

// The four headline figures. "Liquid but encumbered" is the one nobody tracks and the one
// that matters — a liquid asset with a pledge against it is not available.
export function HeadlineTiles({ headline }) {
  return (
    <div className="mb-4 grid grid-cols-2 gap-2 sm:gap-3 lg:grid-cols-4">
      <Tile tone="ok" label="Free and liquid" value={fmtM(headline?.free_and_liquid)}
            sub="Available without consent" />
      <Tile tone="warn" label="Liquid but encumbered" value={fmtM(headline?.liquid_but_encumbered)}
            sub="Requires release before use" />
      <Tile label="Illiquid" value={fmtM(headline?.illiquid)} sub="Run-off and retained income" />
      <Tile label="Total book" value={fmtM(headline?.total)}
            sub={headline ? `${fmtPct(headline.free_and_liquid_share)} free and liquid` : '—'} />
    </div>
  );
}

export function Groups({ groups }) {
  if (!groups?.length) return <Empty>No positions match these filters.</Empty>;
  const total = groups.reduce((t, g) => t + g.value, 0);
  return (
    <div className="divide-y divide-gray-100 dark:divide-gray-800">
      {groups.map((g) => (
        <div key={g.key} className="py-3 first:pt-0 last:pb-0">
          <div className="mb-1.5 flex items-baseline gap-2">
            <span className="text-[13px] font-medium text-gray-900 dark:text-gray-100">{g.label}</span>
            <span className="ml-auto font-serif text-sm text-gray-900 dark:text-gray-100">{fmtM(g.value)}</span>
            <span className="w-11 text-right text-[11px] text-gray-400">{fmtPct(g.share, 1)}</span>
          </div>
          <StackedBar total={g.value} parts={g.parts.map((p) => ({ ...p, color: colorFor(p.key) }))} />
          <div className="mt-1.5 flex flex-wrap gap-x-3 gap-y-1">
            {g.parts.map((p) => (
              <span key={p.key} className="flex items-center gap-1.5 text-[11px] text-gray-500 dark:text-gray-400">
                <i className="inline-block h-2 w-2 rounded-sm" style={{ background: colorFor(p.key) }} />
                {p.label} {fmtM(p.value)}
              </span>
            ))}
          </div>
        </div>
      ))}
      <div className="pt-3 text-right text-[11px] text-gray-400">Total {fmtM(total)}</div>
    </div>
  );
}

// Liquidity x encumbrance. Every cell is an amount and the cells sum exactly to the total
// book — only true because encumbrance is apportioned server-side rather than bucketed
// whole-position.
export function Matrix({ matrix }) {
  if (!matrix?.rows?.length) return <Empty>No positions match these filters.</Empty>;
  return (
    <div className="scrollbar-thin -mx-3 overflow-x-auto px-3 sm:mx-0 sm:px-0">
      <table className="w-full min-w-[440px] text-right text-[13px]">
        <thead>
          <tr className="border-b border-gray-200 dark:border-gray-700">
            <th className="py-2 pr-2 text-left"><Label>Liquidity</Label></th>
            {matrix.columns.map((c) => (
              <th key={c.key} className="py-2 pl-2"><Label className="text-right">{c.label}</Label></th>
            ))}
            <th className="py-2 pl-2"><Label className="text-right">Total</Label></th>
          </tr>
        </thead>
        <tbody>
          {matrix.rows.map((r) => (
            <tr key={r.key} className="border-b border-gray-100 dark:border-gray-800">
              <td className="py-2 pr-2 text-left font-medium text-gray-900 dark:text-gray-100">{r.label}</td>
              {r.cells.map((v, i) => (
                <td key={matrix.columns[i].key} className="py-2 pl-2 tabular-nums text-gray-700 dark:text-gray-300">
                  {v ? fmtM(v) : '—'}
                </td>
              ))}
              <td className="py-2 pl-2 tabular-nums font-medium text-gray-900 dark:text-gray-100">{fmtM(r.total)}</td>
            </tr>
          ))}
          <tr className="bg-gray-50 dark:bg-gray-900/40">
            <td className="py-2 pr-2 text-left font-medium text-gray-900 dark:text-gray-100">Total</td>
            {matrix.column_totals.map((v, i) => (
              <td key={matrix.columns[i].key} className="py-2 pl-2 tabular-nums font-medium text-gray-900 dark:text-gray-100">
                {fmtM(v)}
              </td>
            ))}
            <td className="py-2 pl-2 tabular-nums font-medium text-gray-900 dark:text-gray-100">{fmtM(matrix.total)}</td>
          </tr>
        </tbody>
      </table>
    </div>
  );
}

// House rule: every data table is sortable by any column, via the shared primitive.
const POSITION_COLS = [
  { key: 'name', type: 'string', get: (r) => r.name },
  { key: 'book', type: 'string', get: (r) => r.book_display },
  { key: 'liquidity', type: 'string', get: (r) => r.liquidity_display },
  { key: 'encumbrance', type: 'string', get: (r) => r.encumbrance_display },
  { key: 'value', type: 'number', get: (r) => r.carrying_value },
  { key: 'free', type: 'number', get: (r) => r.free_value },
];

export function PositionTable({ rows }) {
  const { sorted, toggle, indicator } = useSortableRows(rows, POSITION_COLS, { key: 'value', dir: 'desc' });
  if (!rows?.length) return <Empty>No positions in the register yet.</Empty>;
  return (
    <div className="scrollbar-thin -mx-3 overflow-x-auto px-3 sm:mx-0 sm:px-0">
      <table className="w-full min-w-[620px] text-[13px]">
        <thead>
          <tr className="border-b border-gray-200 text-[9px] uppercase tracking-[2px] text-gray-400 dark:border-gray-700 dark:text-gray-500">
            <SortableTh label="Position" colKey="name" toggle={toggle} indicator={indicator} align="left" className="py-2 pr-2 font-normal" />
            <SortableTh label="Book" colKey="book" toggle={toggle} indicator={indicator} align="left" className="py-2 px-2 font-normal" />
            <SortableTh label="Liquidity" colKey="liquidity" toggle={toggle} indicator={indicator} align="left" className="py-2 px-2 font-normal" />
            <SortableTh label="Encumbrance" colKey="encumbrance" toggle={toggle} indicator={indicator} align="left" className="py-2 px-2 font-normal" />
            <SortableTh label="Value" colKey="value" toggle={toggle} indicator={indicator} className="py-2 px-2 font-normal" />
            <SortableTh label="Free" colKey="free" toggle={toggle} indicator={indicator} className="py-2 pl-2 font-normal" />
          </tr>
        </thead>
        <tbody>
          {sorted.map((p) => (
            <tr key={p.id} className="border-b border-gray-100 dark:border-gray-800">
              <td className="py-2 pr-2">
                <div className="text-gray-900 dark:text-gray-100">{p.name}</div>
                {p.constraint_note && (
                  <div className="text-[11px] text-gray-400 dark:text-gray-500">{p.constraint_note}</div>
                )}
              </td>
              <td className="px-2 text-gray-500 dark:text-gray-400">{p.book_display}</td>
              <td className="px-2"><Chip className={LIQ_CHIP[p.liquidity_tier]}>{p.liquidity_display}</Chip></td>
              <td className="px-2"><Chip className={AVAIL[p.encumbrance_status]?.chip}>{p.encumbrance_display}</Chip></td>
              <td className="px-2 text-right tabular-nums text-gray-900 dark:text-gray-100">{fmtM(p.carrying_value)}</td>
              {/* Free value, not carrying value, is what is actually available — both are
                  shown so the gap between them is visible on the row itself. */}
              <td className={`pl-2 text-right tabular-nums ${p.free_value < p.carrying_value ? AVAIL.partial.text : AVAIL.free.text}`}>
                {fmtM(p.free_value)}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
